import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Check, ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, CircleX, Clock, Eye, Filter, GraduationCap, Lightbulb,
  MessageCircleQuestion, NotebookPen, Pencil, Pin, Repeat, RotateCcw, Save, Sparkles, Target,
} from 'lucide-react';
import { errorService, topicService, courseService } from '../services/api';
import { CourseContext } from '../contexts/CourseContext';
import { ErrorAnalysisDraft } from '../components/ai/ErrorAnalysisDraft';
import { aiErrorInfo, aiService } from '../services/api';
import {
  AppShell, Badge, Breadcrumbs, Button, EmptyState, Field, Loader, PageHeader, ScoreCard, Segmented,
} from '../components/ui';
import './ErrorNotebook.css';

const PAGE_SIZE = 20;

function ErrorNotebook() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse } = useContext(CourseContext);
  const [errors, setErrors] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ topicId: null, showMastered: false });
  const [editingError, setEditingError] = useState(null);
  const [editValues, setEditValues] = useState({});
  // Rascunhos da análise por IA, por erro: { loading, data, error }
  const [aiDrafts, setAiDrafts] = useState({});
  const [statusFilter, setStatusFilter] = useState('unreviewed');
  // null = padrão (só o primeiro aberto); depois do primeiro clique vira um Set explícito
  const [openIds, setOpenIds] = useState(null);
  const [expandAll, setExpandAll] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Helper function to build paths with courseSlug
  const buildPath = (path) => {
    return courseSlug ? `/curso/${courseSlug}${path}` : path;
  };

  // Select course when component mounts
  useEffect(() => {
    if (courseSlug) {
      selectCourse(courseSlug);
    }
  }, [courseSlug, selectCourse]);

  useEffect(() => {
    loadData();
  }, [filter, courseSlug]);

  const loadData = async () => {
    try {
      // Build error query params with courseSlug filter
      // Sem tópico: mais recentes primeiro; com tópico, a API ordena pelos mais frequentes
      const errorParams = { topicId: filter.topicId };
      if (courseSlug) {
        errorParams.courseSlug = courseSlug;
      }

      // Load topics filtered by course if courseSlug provided
      const topicsPromise = courseSlug
        ? courseService.getTopicsByCourse(courseSlug)
        : topicService.getAllTopics(false);

      const [errorsData, topicsData] = await Promise.all([
        errorService.getAllErrors(errorParams),
        topicsPromise,
      ]);
      setErrors(errorsData);
      setTopics(topicsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAnalysis = async (errorId) => {
    try {
      await errorService.updateAnalysis(errorId, editValues);
      setEditingError(null);
      setEditValues({});
      loadData();
    } catch (error) {
      console.error('Error saving analysis:', error);
    }
  };

  const handleMarkReviewed = async (errorId) => {
    try {
      await errorService.markAsReviewed(errorId);
      loadData();
    } catch (error) {
      console.error('Error marking as reviewed:', error);
    }
  };

  const [lastMastered, setLastMastered] = useState(null);

  const handleMarkMastered = async (errorId) => {
    try {
      await errorService.markAsMastered(errorId);
      setLastMastered(errorId);
      loadData();
    } catch (error) {
      console.error('Error marking as mastered:', error);
    }
  };

  const analyzeWithAI = async (error) => {
    setAiDrafts(d => ({ ...d, [error.id]: { loading: true } }));
    try {
      const data = await aiService.analyzeError(error.id);
      setAiDrafts(d => ({ ...d, [error.id]: { data } }));
    } catch (err) {
      setAiDrafts(d => ({ ...d, [error.id]: { error: aiErrorInfo(err) } }));
    }
  };

  const discardDraft = (id) => setAiDrafts(d => {
    const copy = { ...d };
    delete copy[id];
    return copy;
  });

  const saveDraft = async (id, data) => {
    try {
      await errorService.updateAnalysis(id, data);
      discardDraft(id);
      loadData();
    } catch (error) {
      console.error('Error saving analysis:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const unreviewed = errors.filter((e) => !e.reviewed).length;
  const reviewedCount = errors.length - unreviewed;
  const occurrences = errors.reduce((acc, e) => acc + (e.timesWrong || 1), 0);

  const filtered = useMemo(() => errors.filter((e) => (
    statusFilter === 'all' || (statusFilter === 'reviewed' ? e.reviewed : !e.reviewed)
  )), [errors, statusFilter]);
  const visible = filtered.slice(0, limit);

  const isOpen = (error, index) => {
    if (expandAll) return true;
    return openIds ? openIds.has(error.id) : index === 0;
  };

  const toggleOpen = (error, index) => {
    const next = new Set(expandAll ? visible.map((e) => e.id) : openIds || (visible[0] ? [visible[0].id] : []));
    if (isOpen(error, index)) next.delete(error.id);
    else next.add(error.id);
    setExpandAll(false);
    setOpenIds(next);
  };

  const toggleAll = () => {
    setExpandAll(!expandAll);
    setOpenIds(expandAll ? new Set() : null);
  };

  const changeStatus = (value) => {
    setStatusFilter(value);
    setLimit(PAGE_SIZE);
    setOpenIds(null);
  };

  const topicName = (error) => error.topic?.name || error.question?.category?.name;

  const formatShortDate = (dateString) => (dateString
    ? new Date(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    : '');

  const startEditing = (error) => {
    setEditingError(error.id);
    setEditValues({
      errorReason: error.errorReason || '',
      simpleRule: error.simpleRule || '',
      variation1: error.variation1 || '',
      variation2: error.variation2 || '',
    });
  };

  if (loading) {
    return (
      <AppShell section="Caderno de erros" courseName={currentCourse?.name}>
        <Loader label="Carregando caderno de erros..." />
      </AppShell>
    );
  }

  return (
    <AppShell section="Caderno de erros" courseName={currentCourse?.name}>
      <div className="ss-page ss-page--crumbs">
      <Breadcrumbs items={[{ label: 'Modo Estudo', onClick: () => navigate(buildPath('/estudo')) }, { label: 'Caderno de erros' }]} />
      <PageHeader
        icon={NotebookPen}
        eyebrow={currentCourse?.name}
        title="Caderno de erros"
        subtitle="Entenda por que errou, escreva uma regra e marque como dominado."
        actions={
          <>
            <Button variant="outline" icon={Target} onClick={() => navigate(buildPath('/config'))}>Novo simulado</Button>
            <Button variant="accent" icon={RotateCcw} onClick={() => navigate(buildPath('/config?style=errors'))} disabled={errors.length === 0} data-tour="erros-simulado">
              Simulado dos meus erros
            </Button>
          </>
        }
      />

      {lastMastered && (
        <div className="en-undo" role="status">
          <GraduationCap size={18} aria-hidden="true" />
          <span>Erro marcado como dominado e removido da lista.</span>
          <Button size="sm" variant="outline" onClick={async () => {
            await errorService.markAsMastered(lastMastered, false);
            setLastMastered(null);
            loadData();
          }}>
            Desfazer
          </Button>
          <button type="button" className="ss-iconbtn" onClick={() => setLastMastered(null)} aria-label="Fechar">×</button>
        </div>
      )}

      <ScoreCard
        inline
        items={[
          { icon: NotebookPen, value: errors.length, label: 'Questões', hint: 'no caderno', tone: 'danger' },
          { icon: Eye, value: unreviewed, label: 'Sem análise', hint: 'ainda não revisadas', tone: 'lime' },
          { icon: Repeat, value: occurrences, label: 'Erros', hint: 'somando repetições' },
        ]}
      />

      <div className="en-toolbar" data-tour="erros-filtro">
        <Segmented
          ariaLabel="Filtrar por status"
          value={statusFilter}
          onChange={changeStatus}
          options={[
            { value: 'unreviewed', label: 'Não revisados', count: unreviewed },
            { value: 'reviewed', label: 'Revisados', count: reviewedCount },
            { value: 'all', label: 'Todos', count: errors.length },
          ]}
        />
        <label className="en-filter">
          <Filter size={13} aria-hidden="true" />
          <select
            value={filter.topicId || ''}
            onChange={(e) => { setFilter({ ...filter, topicId: e.target.value || null }); setLimit(PAGE_SIZE); setOpenIds(null); }}
            aria-label="Filtrar por tópico"
          >
            <option value="">Todos os tópicos</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
            ))}
          </select>
          <ChevronDown size={12} aria-hidden="true" />
        </label>
        <span className="en-toolbar__count">
          {filtered.length} {filtered.length === 1 ? 'erro' : 'erros'} · {filter.topicId ? 'mais frequentes primeiro' : 'mais recentes primeiro'}
        </span>
        {filtered.length > 0 && (
          <button type="button" className="en-toggleall" onClick={toggleAll}>
            {expandAll ? <ChevronsDownUp size={13} aria-hidden="true" /> : <ChevronsUpDown size={13} aria-hidden="true" />}
            {expandAll ? 'Recolher todos' : 'Expandir todos'}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        errors.length === 0 ? (
          <EmptyState
            file="erros.xml"
            title="Nenhum erro para revisar!"
            description="Continue praticando com os simulados. Os erros aparecem aqui automaticamente."
            action={<Button icon={Target} onClick={() => navigate(buildPath('/config'))}>Fazer simulado</Button>}
          />
        ) : (
          <EmptyState
            file="erros.xml"
            title={statusFilter === 'reviewed' ? 'Nenhum erro revisado ainda' : 'Tudo revisado!'}
            description={statusFilter === 'reviewed'
              ? 'Analise um erro e marque como revisado para ele aparecer aqui.'
              : 'Você já revisou todos os erros deste filtro.'}
            action={<Button variant="outline" onClick={() => changeStatus('all')}>Ver todos</Button>}
          />
        )
      ) : (
        <div className={`en-list ${expandAll ? 'en-list--open' : ''}`}>
          {visible.map((error, index) => {
            const options = error.question?.options || [];
            const userLetters = parseLetters(error.userAnswer);
            const correctLetters = parseLetters(error.correctAnswer);
            const explanation = error.question?.explanation;
            const question = error.questionText || error.question?.questionText || 'Questão não disponível';
            const topic = topicName(error);
            const times = error.timesWrong || 1;

            if (!isOpen(error, index)) {
              return (
                <button
                  key={error.id}
                  type="button"
                  className={`en-row ${error.reviewed ? 'en-row--reviewed' : ''}`}
                  onClick={() => toggleOpen(error, index)}
                  aria-expanded="false"
                >
                  <span className="en-row__times">{times}x</span>
                  <span className="en-row__q">{question}</span>
                  {error.reviewed && <Check size={14} className="en-row__check" aria-label="Revisado" />}
                  {topic && <span className="en-tag">{topic}</span>}
                  {error.errorReason
                    ? <span className="en-row__ai is-done"><Check size={12} aria-hidden="true" />analisado</span>
                    : <span className="en-row__ai"><Sparkles size={12} aria-hidden="true" />Analisar com IA</span>}
                  <span className="en-row__date">{formatShortDate(error.lastWrongAt)}</span>
                  <ChevronDown size={18} className="en-row__chev" aria-hidden="true" />
                </button>
              );
            }

            return (
              <article key={error.id} className={`en-card ${error.reviewed ? 'en-card--reviewed' : ''}`} data-tour="erros-item">
                <div className="en-card__head">
                  <Badge tone="danger" icon={CircleX}>Errou {times}x</Badge>
                  {error.reviewed && <Badge tone="success" icon={Check}>Revisado</Badge>}
                  {topic && <span className="en-tag">{topic}</span>}
                  <span className="en-card__date"><Clock size={12} aria-hidden="true" /> {formatDate(error.lastWrongAt)}</span>
                  {!aiDrafts[error.id] && editingError !== error.id && (
                    <button
                      type="button"
                      className="en-card__ai"
                      onClick={() => analyzeWithAI(error)}
                      title="A IA preenche motivo, regra e 2 variações como rascunho"
                    >
                      <Sparkles size={13} aria-hidden="true" /> Analisar com IA
                    </button>
                  )}
                  <button
                    type="button"
                    className="en-card__collapse"
                    onClick={() => toggleOpen(error, index)}
                    aria-expanded="true"
                    aria-label="Recolher"
                  >
                    <ChevronUp size={18} aria-hidden="true" />
                  </button>
                </div>

                <p className="en-card__q">{question}</p>

                <div className="en-answers">
                  <AnswerBox tone="bad" label="Sua resposta" letters={userLetters} options={options} />
                  <AnswerBox tone="ok" label="Resposta correta" letters={correctLetters} options={options} />
                </div>

                {aiDrafts[error.id] && editingError !== error.id && (
                  <ErrorAnalysisDraft
                    draft={aiDrafts[error.id]}
                    onRetry={() => analyzeWithAI(error)}
                    onDiscard={() => discardDraft(error.id)}
                    onSave={(data) => saveDraft(error.id, data)}
                    onEdit={(data) => { discardDraft(error.id); setEditingError(error.id); setEditValues(data); }}
                  />
                )}

                {editingError === error.id ? (
                  <div className="en-edit">
                    <Field label="Motivo do erro">
                      <textarea
                        value={editValues.errorReason ?? error.errorReason ?? ''}
                        onChange={(e) => setEditValues({ ...editValues, errorReason: e.target.value })}
                        placeholder="Por que você errou? (Ex: Confundi os conceitos X e Y)"
                        rows={2}
                      />
                    </Field>
                    <Field label="Regra simples">
                      <input
                        type="text"
                        value={editValues.simpleRule ?? error.simpleRule ?? ''}
                        onChange={(e) => setEditValues({ ...editValues, simpleRule: e.target.value })}
                        placeholder="Uma frase para lembrar (Ex: X sempre usa Y)"
                      />
                    </Field>
                    <div className="en-edit__two">
                      <Field label="Variação 1">
                        <textarea
                          value={editValues.variation1 ?? error.variation1 ?? ''}
                          onChange={(e) => setEditValues({ ...editValues, variation1: e.target.value })}
                          placeholder="Questão variante para praticar..."
                          rows={2}
                        />
                      </Field>
                      <Field label="Variação 2">
                        <textarea
                          value={editValues.variation2 ?? error.variation2 ?? ''}
                          onChange={(e) => setEditValues({ ...editValues, variation2: e.target.value })}
                          placeholder="Outra questão variante..."
                          rows={2}
                        />
                      </Field>
                    </div>
                    <div className="en-edit__actions">
                      <Button variant="ghost" onClick={() => { setEditingError(null); setEditValues({}); }}>Cancelar</Button>
                      <Button icon={Save} onClick={() => handleSaveAnalysis(error.id)}>Salvar análise</Button>
                    </div>
                  </div>
                ) : (
                  (error.errorReason || error.simpleRule || error.variation1 || error.variation2) && (
                    <div className="en-notes">
                      {error.errorReason && <Note icon={MessageCircleQuestion} label="Motivo">{error.errorReason}</Note>}
                      {error.simpleRule && <Note icon={Pin} label="Regra" highlight>{error.simpleRule}</Note>}
                      {error.variation1 && <Note icon={Repeat} label="Variação 1">{error.variation1}</Note>}
                      {error.variation2 && <Note icon={Repeat} label="Variação 2">{error.variation2}</Note>}
                    </div>
                  )
                )}

                {editingError !== error.id && !aiDrafts[error.id] && (
                  <div className="en-card__foot">
                    {explanation && explanation.trim() && (
                      <details className="en-explain">
                        <summary><Lightbulb size={14} aria-hidden="true" /> Ver explicação</summary>
                        <p>{explanation}</p>
                      </details>
                    )}
                    <div className="en-card__actions">
                      <Button size="sm" variant="ghost" icon={Pencil} onClick={() => startEditing(error)}>Analisar</Button>
                      {!error.reviewed && (
                        <Button size="sm" variant="outline" className="en-btn-navy" icon={Check} onClick={() => handleMarkReviewed(error.id)}>
                          Marcar revisado
                        </Button>
                      )}
                      <Button size="sm" variant="accent" icon={GraduationCap} onClick={() => handleMarkMastered(error.id)} title="Remove da lista de erros">
                        Dominei
                      </Button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {filtered.length > limit && (
        <div className="en-more">
          <Button variant="outline" iconRight={ChevronDown} onClick={() => setLimit(limit + PAGE_SIZE)}>
            Carregar mais {Math.min(PAGE_SIZE, filtered.length - limit)}
          </Button>
        </div>
      )}
      </div>

    </AppShell>
  );
}

// As respostas são gravadas como JSON (ex.: '["A","C"]'); aceita também texto simples.
function parseLetters(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return String(raw).split(',').map(s => s.trim()).filter(Boolean);
  }
}

function AnswerBox({ tone, label, letters, options }) {
  return (
    <div className={`en-answer en-answer--${tone}`}>
      <span className="en-answer__label">{label}</span>
      {letters.length === 0 ? (
        <span className="en-answer__empty">Sem resposta</span>
      ) : (
        letters.map(letter => {
          const opt = options.find(o => o.optionLetter === letter);
          return (
            <div key={letter} className="en-answer__opt">
              <span className="en-answer__letter">{letter}</span>
              {opt && <span>{opt.optionText}</span>}
            </div>
          );
        })
      )}
    </div>
  );
}

function Note({ icon: Icon, label, highlight, children }) {
  return (
    <div className={`en-note ${highlight ? 'en-note--rule' : ''}`}>
      <Icon size={16} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default ErrorNotebook;
